/**
 * The words the anomaly console speaks.
 *
 * Kept apart from the screen because this is the part that has to be right for
 * someone who did not build the detector. "over_voltage, warning" is only
 * meaningful if you already know that a phase carrying current is judged
 * differently from an idle one, and that the charger protects itself against
 * supply problems so we deliberately never pull the plug for one. Spelled out,
 * that stops being knowledge you have to have and becomes something the page
 * tells you.
 *
 * Every kind carries three things: what was actually measured, what it means
 * for the charger or the customer, and what -- if anything -- the person
 * reading is expected to do about it.
 */

export const asUtc = (s: string) =>
  /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`;

export const dt = (s: string | null | undefined) =>
  s
    ? new Date(asUtc(s)).toLocaleString(undefined, {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : "—";

/** "4 minutes ago", "2 days ago" -- how long a charger has been like this. */
export const ago = (s: string | null | undefined): string => {
  if (!s) return "—";
  const secs = (Date.now() - new Date(asUtc(s)).getTime()) / 1000;
  if (secs < 90) return "just now";
  const units: Array<[number, string]> = [
    [60, "minute"], [3600, "hour"], [86400, "day"], [604800, "week"],
  ];
  for (let i = units.length - 1; i >= 0; i -= 1) {
    const [size, name] = units[i];
    if (secs >= size) {
      const n = Math.floor(secs / size);
      return `${n} ${name}${n > 1 ? "s" : ""} ago`;
    }
  }
  return "a while";
};

/** How long a condition lasted, from a second count. */
export const lasted = (seconds: number | string | null | undefined): string => {
  const s = Number(seconds ?? 0);
  if (!s || s < 60) return `${Math.max(Math.round(s), 0)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

// Postgres numeric arrives as a STRING, so every figure goes through Number()
// before formatting. Without it toLocaleString is a no-op on the string and
// renders things like "0.63650000000000000000 kWh".
const fmt = (n: number | string | null | undefined, d: number) =>
  Number(n ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: d, maximumFractionDigits: d,
  });

export const inr = (n: number | string | null | undefined) => `₹${fmt(n, 2)}`;
export const kwh = (n: number | string | null | undefined) => `${fmt(n, 2)} kWh`;
export const wh = (n: number | string | null | undefined) => `${fmt(n, 0)} Wh`;
export const count = (n: number | string | null | undefined) => fmt(n, 0);

/** A measured value with its unit, e.g. "196.0 V". */
export const measured = (
  v: number | string | null | undefined,
  unit: string | null | undefined,
) => (v == null ? "—" : `${fmt(v, 1)}${unit ? ` ${unit}` : ""}`);

export type Tone = "ok" | "bad" | "warn" | "muted" | "info";

export interface SeverityStory {
  label: string;
  tone: Tone;
  /** What this severity is permitted to do. */
  means: string;
}

export const severityStory = (severity: string): SeverityStory => {
  switch (severity) {
    case "critical":
      return {
        label: "Critical",
        tone: "bad",
        means: "Mailed immediately. The only level that may stop a session and reset a charger.",
      };
    case "warning":
      return {
        label: "Warning",
        tone: "warn",
        means: "Mailed once when it starts and once when it clears. Never touches the charger.",
      };
    default:
      return {
        label: "Info",
        tone: "muted",
        means: "Recorded only, never mailed. Normal readings that are worth keeping, not acting on.",
      };
  }
};

export const stateStory = (state: string): { label: string; tone: Tone } => {
  switch (state) {
    case "suspect":   return { label: "Open", tone: "warn" };
    case "probing":   return { label: "Confirming", tone: "info" };
    case "confirmed": return { label: "Confirmed", tone: "bad" };
    case "cleared":   return { label: "Recovered", tone: "ok" };
    default:          return { label: "Expired", tone: "muted" };
  }
};

/** An action that was withheld matters as much as one that fired. */
export const actionStory = (state: string): { label: string; tone: Tone } | null => {
  switch (state) {
    case "sent":     return { label: "sent", tone: "bad" };
    case "deferred": return { label: "deferred — charger still needs a human", tone: "warn" };
    case "failed":   return { label: "failed", tone: "bad" };
    case "skipped":  return { label: "not applicable", tone: "muted" };
    case "disabled": return { label: "disabled by config", tone: "muted" };
    default:         return null;
  }
};

export interface KindStory {
  label: string;
  /** What the reading actually is. */
  means: string;
  /** What someone should do about it. */
  next: string;
}

export const kindStory = (kind: string): KindStory => {
  switch (kind) {
    case "over_power":
    case "over_current":
      return {
        label: kind === "over_power" ? "Drawing too much power" : "Drawing too much current",
        means: "The charger pulled materially more than the ceiling it told us it would offer the car.",
        next: "Inspect the connector, cable and contactor for heat damage before returning it to service.",
      };
    case "over_voltage":
      return {
        label: "Supply voltage high",
        means: "Incoming voltage above the statutory band. A phase carrying no current normally reads high, so an idle-phase reading is recorded as info rather than treated as a fault.",
        next: "Nothing urgent. If it persists on a loaded phase, raise it with the site electrical team.",
      };
    case "under_voltage":
      return {
        label: "Supply voltage sagging under load",
        means: "Voltage fell below the statutory band while the connector was drawing. Current is pilot-limited, so the car simply charges slower rather than pulling more.",
        next: "Measure voltage at the distribution board and at the charger under load. A large gap means the loss is in the run between them.",
      };
    case "over_temperature":
    case "temperature_rise":
      return {
        label: "Charger running hot",
        means: "Internal temperature well above what the rest of the fleet runs at.",
        next: "Check ventilation, fans and ambient conditions at the cabinet. Look for dust and heat damage around the contactor.",
      };
    case "phase_imbalance":
      return {
        label: "Phases unbalanced",
        means: "The phases actually drawing current are significantly uneven. An idle phase is not counted.",
        next: "Check phase connections and balancing at the distribution board.",
      };
    case "frequency_deviation":
      return {
        label: "Supply frequency off",
        means: "Powerline frequency outside 47.5–52 Hz.",
        next: "Grid condition. Worth noting if it recurs at one site.",
      };
    case "low_power_factor":
      return {
        label: "Implausible power factor",
        means: "Power factor far below what real load looks like while drawing power.",
        next: "Usually a meter reporting oddly rather than a real electrical problem.",
      };
    case "charger_error_code":
      return {
        label: "Charger raised a fault",
        means: "The charger's own verdict, not something we inferred from readings.",
        next: "How seriously to take each code is set on the Error code ranking tab.",
      };
    default:
      return { label: kind, means: "", next: "" };
  }
};

export interface SourceStory {
  label: string;
  tone: Tone;
  means: string;
}

/** How much to trust a recovered energy figure. */
export const sourceStory = (source: string): SourceStory => {
  if (source === "register_delta") {
    return {
      label: "exact",
      tone: "ok",
      means: "Real meter data, reassembled from the charger's own forward increments. As trustworthy as an ordinary session.",
    };
  }
  if (source === "power_active") {
    return {
      label: "estimated",
      tone: "warn",
      means: "Integrated from the charger's reported power, because its energy register never moved.",
    };
  }
  if (source === "volt_amp" || source === "nominal_volt_amp") {
    return {
      label: "estimated",
      tone: "warn",
      means:
        source === "volt_amp"
          ? "Integrated from measured volts × amps, because the register was unusable."
          : "Integrated from measured amps at an assumed 230 V — this charger reports neither power nor voltage.",
    };
  }
  return { label: source || "—", tone: "muted", means: "" };
};

export const faultStory = (kind: string): string => {
  switch (kind) {
    case "register_frozen":
      return "The energy register never moved while power was flowing, so nothing could be billed from it.";
    case "register_rollback":
      return "The register jumped backwards mid-session and climbed again, so the session was spent re-reaching its own starting value.";
    case "register_offset":
      return "The charger reported a lifetime reading at the start but counts from zero during the session. The two scales never meet.";
    default:
      return "";
  }
};
